import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { IndividualProjectGraphsStyles as styles } from './individual-projects-graphs.styles';
import {
  IndividualProjectGraphsTemplate as template,
  TAB_LIST,
} from './individual-projects-graphs.template';
import { IVppaService } from '../../../../services';
import { logger } from '@genesislcap/foundation-comms';
import { IProjectForecastTs, IProjectHistoricalTs, IVppa } from '../../../../types';
import { Generation } from '../graphs/generation/generation';
import { LmeTotalCarbonReduction } from '../graphs/lme-total-carbon-reduction/lme-total-carbon-reduction';
import { CarbonFreeEnergy } from '../graphs/carbon-free-energy/carbon-free-energy';
import { NodalBasis } from '../graphs/nodal-basis/nodal-basis';
import { SettlementForecast } from '../graphs/settlement-forecast/settlement-forecast';
import { FloatingPrice } from '../graphs/floating-price/floating-price';
import { ImpliedCarbonPrice } from '../graphs/implied-carbon-price/implied-carbon-price';
import { notifyError } from '../../../../utils/notify';
import { PRODUCT_TYPE } from '../../../../utils';
import { RecImpliedCarbonPrice } from '../graphs/rec-implied-carbon-price/rec-implied-carbon-price';

const name = 'individual-projects-graphs';

@customElement({
  name,
  template,
  styles,
})
export class IndividualProjectGraphs extends FASTElement {
  @IVppaService vppaService: IVppaService;

  @attr selectedVppa: IVppa;
  @attr megawattAC: number;
  @attr @observable selectedProductType: PRODUCT_TYPE;
  @observable activeTab: TAB_LIST = TAB_LIST.GENERATION;

  @observable historicalData: Array<IProjectHistoricalTs>;
  @observable forecastData: Array<IProjectForecastTs>;

  @observable generationGraph: Generation;
  @observable lmeCarbonReductionGraph: LmeTotalCarbonReduction;
  @observable carbonFreeEnergyGraph: CarbonFreeEnergy;
  @observable nodalBasisGraph: NodalBasis;
  @observable settlementForecastGraph: SettlementForecast;
  @observable floatingPriceGraph: FloatingPrice;
  @observable impliedCarbonPriceGraph: ImpliedCarbonPrice;
  @observable recImpliedCarbonPriceGraph: RecImpliedCarbonPrice;

  connectedCallback() {
    super.connectedCallback();
    if (this.selectedVppa) {
      this.selectedVppaChanged(null, this.selectedVppa);
    }
  }
  public selectedVppaChanged(oldValue: IVppa, newValue: IVppa) {
    this.clearGraphs();
    if (this.selectedVppa != null && newValue) {
      this.vppaService.getVppaHistoricalTs(newValue.VPPA_ID).then((response) => {
        if (response && response.MESSAGE_TYPE !== 'EVENT_NACK') {
          if (this.selectedVppa?.VPPA_ID == newValue.VPPA_ID) {
            this.historicalData = response.REPLY.sort(
              (a, b) => Number(a.METRICS_DATETIME) - Number(b.METRICS_DATETIME),
            );

            this.generationGraph?.renderGraph(
              this.historicalData,
              this.forecastData,
              this.selectedVppa?.OFFERED_CAPACITY,
              this.megawattAC,
            );
            this.lmeCarbonReductionGraph?.renderGraph(this.historicalData, this.forecastData);
            this.carbonFreeEnergyGraph?.renderGraph(this.historicalData);
            this.nodalBasisGraph?.renderGraph(this.historicalData);
          }
        } else {
          notifyError(response.ERROR[0].TEXT);
          logger.error(response.ERROR);
        }
      });

      this.vppaService
        .getVppaForecastTs(newValue.VPPA_ID)
        .then((response) => {
          if (response && response.MESSAGE_TYPE !== 'EVENT_NACK') {
            if (this.selectedVppa?.VPPA_ID == newValue.VPPA_ID) {
              this.forecastData = response.REPLY.sort(
                (a, b) => Number(a.DATETIME) - Number(b.DATETIME),
              );
              const startDate = new Date(newValue.TENOR_START_DATE);
              const startTimestamp = startDate.getTime();
              const startDateMonth = startDate.getUTCMonth();
              const tenorMonths = newValue.TENOR_MONTHS;
              const month = startDateMonth + tenorMonths;
              const endTime = new Date(startDate.setUTCMonth(month));
              const endtimestamp = endTime.getTime();
              this.forecastData = this.forecastData.filter(
                (data) => data.METRICS_DATETIME >= startTimestamp && data.METRICS_DATETIME < endtimestamp,
              );

              this.generationGraph?.renderGraph(
                this.historicalData,
                this.forecastData,
                this.selectedVppa?.OFFERED_CAPACITY,
                this.megawattAC,
              );
              this.lmeCarbonReductionGraph?.renderGraph(this.historicalData, this.forecastData);

              this.settlementForecastGraph?.renderGraph(
                this.forecastData,
                startTimestamp,
                endtimestamp,
              );
              this.floatingPriceGraph?.renderGraph(
                this.forecastData,
                newValue.FIXED_PRICE,
                startTimestamp,
                endtimestamp,
              );
              this.impliedCarbonPriceGraph?.renderGraph(
                this.forecastData,
                startTimestamp,
                endtimestamp,
              );
              this.recImpliedCarbonPriceGraph?.renderGraph(
                this.forecastData,
                startTimestamp,
                endtimestamp,
                this.selectedVppa.FIXED_PRICE,
              );
            }
          } else {
            notifyError(response.ERROR[0].TEXT);
            logger.error(response);
          }
        })
        .catch((err) => {
          logger.error(err);
        });
    }
  }

  public tabSelected(tab: TAB_LIST) {
    if (this.activeTab !== tab) {
      // Only reflow the graphs if a new tab is selected
      this.activeTab = tab;
      switch (tab) {
        case TAB_LIST.GENERATION:
          this.generationGraph.reflow();
          break;
        case TAB_LIST.LME_TOTAL_CARBON:
          this.lmeCarbonReductionGraph.reflow();
          break;
        case TAB_LIST.CARBON_FREE:
          this.carbonFreeEnergyGraph.reflow();
          break;
        case TAB_LIST.NODAL_BASIS:
          this.nodalBasisGraph.reflow();
          break;
        case TAB_LIST.SETTLEMENT_FORECAST:
          this.settlementForecastGraph.reflow();
          break;
        case TAB_LIST.FLOATING_PRICE:
          this.floatingPriceGraph.reflow();
          break;
        case TAB_LIST.IMPLIED_CARBON_PRICE:
          if (this.selectedVppa?.PRODUCT_TYPE === PRODUCT_TYPE.RECS) {
            this.recImpliedCarbonPriceGraph.reflow();
          } else {
            this.impliedCarbonPriceGraph.reflow();
          }
          break;
        default:
          break;
      }
    }
  }

  private clearGraphs() {
    this.forecastData = [];
    this.historicalData = [];
    this.generationGraph?.renderGraph(null, null, null, null);
    this.lmeCarbonReductionGraph?.renderGraph(null, null);
    this.carbonFreeEnergyGraph?.renderGraph(null);
    this.nodalBasisGraph?.renderGraph(null);
    this.settlementForecastGraph?.renderGraph(null, null, null);
    this.floatingPriceGraph?.renderGraph(null, null, null, null);
    this.impliedCarbonPriceGraph?.renderGraph(null, null, null);
    this.recImpliedCarbonPriceGraph?.renderGraph(null, null, null, null);
  }
}
